import { transaction } from '@src/slide/domain/slide-app';
import { createNanoEvents } from 'nanoevents';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import PageExitManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/page-exit-manager';
import UploadQueue from '@src/slide/infrastructure/module/dependent/image/image-toolkit/upload-queue';
import { ImageResource } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-resource';
import { addImagesInSlide, replaceImage } from '@src/slide/application/service/copy-paste/core/cp-image';
import { registerLogger, registerReporter, registerSnackbar } from '../addons';
import { ImageEditEvents, ImageEditEventHandlers } from '../events';
import ImageRecoveryManager from './image-recovery-manager';
import ImageCreator from './image-creator';
import EditorImage from './editor-image';
import { generateFailImageUrl } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache/fail-image';
import i18n from '@i18next/i18n';
import { observable } from 'mobx';
import { browser } from '@utils/agent';
import DUIMobileLoading from '@tencent/dui-mobile/lib/components/Loading';
import DUILoading from '@tencent/dui/lib/components/Loading';
import ImageUploader from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-uploader';
import { IImageEditManager, ImageReplaceAction } from '@src/slide/application/service/copy-paste/core/cp-image.new/image-edit-manager/core/image-edit-manager.interface';
import { ImageCompressOptions } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-compressor';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ContentStatusEvents } from '@tencent/slide-sdk-model/src/manager/content-status-manager';
import ImageOriginalSizeWithDPI from '@src/slide/infrastructure/module/dependent/image/original-size-with-dpi';
import { CDNImage } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/cgi-upload';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import { IImageResourceData } from '@tencent/slide-sdk-model/src/data/resource-map-data';
import { waitForReadyToEdit } from '@src/slide/domain/slide-app/scenes/offline/utils';

export default class ImageEditManager implements IImageEditManager {
    private static instance?: ImageEditManager;

    public static getInstance(): ImageEditManager {
        if (!this.instance) {
            this.instance = new ImageEditManager();
        }
        return this.instance;
    }

    /**
     * 正在进行替换图片操作
     */
    @observable
    public imageReplacing = false;
    // 替换图片动作，2代表是替换背景图片 1是替换前景图
    @observable
    public imageReplaceAction = 0;
    // 等待剪切板数据
    @observable
    public clipDataWaiting = false;
    public enableImageRecovery = true;

    private eventBus = createNanoEvents<ImageEditEventHandlers>();

    private imageCreator: ImageCreator;
    private imageRecovery?: ImageRecoveryManager;

    private menuManager?: IMenuManager;
    private selectionService?: ISelectionService;

    // 记录 input 上传的 accept 类型
    private defaultAccept?: string;

    private constructor() {
        registerSnackbar(this.eventBus);
        registerReporter(this.eventBus);
        registerLogger(this.eventBus);

        this.imageCreator = new ImageCreator(this.eventBus);
        this.performImageRecovery();
    }

    public setServicesDelegate(services: { menuManager?: IMenuManager, selectionService?: ISelectionService }) {
        this.menuManager = this.menuManager ?? services.menuManager;
        this.selectionService = this.selectionService ?? services.selectionService;
    }

    public async createEditorImages(blobs: Blob[]): Promise<EditorImage[]> {
        const ctx = await this.imageCreator.create(blobs);
        this.uploadAndUpdateImages(ctx.resources);
        return ctx.images;
    }

    public async bulkInsertBlobImages(blobs: Blob[]): Promise<void> {
        const images = await this.createEditorImages(blobs);
        ImageOriginalSizeWithDPI.updateImageOriginalSizeByLocalImages(images);
        addImagesInSlide(images, this.selectionService?.slides[this.selectionService.currentSlideIndex]);
    }

    public async bulkReplaceBlobImages(
        blobs: Blob[],
        distModel: any = this.selectionService?.getSelectedModel(),
    ): Promise<void> {
        const images = await this.createEditorImages(blobs);
        replaceImage(images, distModel);
    }

    /**
     * 图片上传（不走图片秒看，直接上传并返回http的图片地址）
     * @param blobs
     */
    public async uploadWithoutQuickLook(
        blobs: Blob[],
        tip = i18n.t('正在上传图片...'),
        compressOptions?: ImageCompressOptions,
    ): Promise<{ id: string, src: string }[]> {
        const loading = (browser.isMobile ? DUIMobileLoading : DUILoading)
            .show({ tip });
        let image: CDNImage | undefined;
        let imageId: string | undefined;
        try {
            SlideDataServices.ContentStatusManager()?.emitter.emit(ContentStatusEvents.ResourceSyncBegin);
            // 目前只用作文本图片填充，所以暂不支持多张图上传，后续有需要再扩展
            image = await ImageUploader.uploadBlob(blobs[0], compressOptions);
            imageId = await ImageQuickLookManager.getInstance().createResourceIdByBlob(blobs[0]);
            this.updateResourceMap(imageId, { src: image.url, ...image });
        } catch (err) {
            this.eventBus.emit(ImageEditEvents.UploadError, err);
        } finally {
            SlideDataServices.ContentStatusManager()?.emitter.emit(ContentStatusEvents.ResourceSyncEnd);
        }
        loading.close();
        return [{ id: imageId ?? '', src: image?.url ?? '' }] ;
    }

    public uploadAndUpdateImages = (resources: ImageResource[]): void => {
        const handleUpload = () => this.eventBus.emit(ImageEditEvents.DidUpload);
        const unbindAll = resources.map(res => res.onDidUpload(handleUpload));
        const quickLook = ImageQuickLookManager.getInstance();

        // 必须在上传前写入 resource map，否则 Gif 不会动
        resources.forEach((resource) => {
            const presentation = SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation();
            if (!presentation) return;
            const { type, size } = resource.getBlob();
            const descriptor: Omit<IImageResourceData, 'id'> = { size, type };
            const { src } = presentation.getImageResourceById(resource.id) ?? {};
            if (ImageUrl.isBase64Url(src ?? '')) {
                // 图片是 base64 格式，可能为占位图，需要重新上传转 CDNImage
                descriptor.src = '';
            }
            presentation.api.updateImageResourceMap(resource.id, descriptor);
        });

        this.uploadImageNonBlocking(resources, {
            onProgress: (resources) => {
                resources.forEach((resource) => {
                    quickLook.mapResourceToUrl(resource, resource.getCdnUrl());
                    this.imageRecovery?.drop(resource.id);
                });
                this.batchUpdateResourceUrls(resources, resources.map(resource => resource.getCdnUrl()));
            },
            onFinish: () => {
                unbindAll.forEach(unbind => unbind());
            },
            onError: async (err: Error & { isNetworkError?: boolean }, resource) => {
                const { width, height } = await resource.loadMeta() ;
                const failImageUrl = generateFailImageUrl(i18n.t('图片上传失败，请稍后再试。'), width, height);

                this.batchUpdateResourceUrls([resource], [failImageUrl]);
                this.eventBus.emit(ImageEditEvents.UploadError, err);

                if (!err.isNetworkError) {
                    this.imageRecovery?.drop(resource.id);
                }
            },
        });
    };

    public replaceImageFromFile = (imageReplaceAction: ImageReplaceAction, acceptType?: string) => {
        // 从文件获取
        const input = document.getElementById('select_pic_input');
        // 记录 accept 类型
        if (!this.defaultAccept) {
            this.defaultAccept = (input?.getAttribute('accept') ?? '') as string;
        }

        input?.setAttribute('accept', acceptType ?? this.defaultAccept);

        input?.click();
        this.imageReplaceAction = imageReplaceAction;
        this.imageReplacing = true;
        this.clipDataWaiting = false;
    };

    public replaceImageFromClipBoard = (imageReplaceAction: ImageReplaceAction) => {
        // 从剪贴板中获取
        ImageEditManager.getInstance().imageReplaceAction = imageReplaceAction; // 代表是替换前景图片
        ImageEditManager.getInstance().clipDataWaiting = true;
    };

    private async uploadImageNonBlocking(
        resources: ImageResource[],
        {
            onProgress = () => {},
            onError = () => {},
            onFinish = () => {},
        }: {
            onProgress?: (resources: ImageResource[]) => void;
            onFinish?: () => void;
            onError?: (err: Error, resource: ImageResource) => void;
        },
    ): Promise<void> {
        if (!resources.length) return;

        const queue = new UploadQueue<ImageResource>();

        PageExitManager.getInstance().protect();
        SlideDataServices.ContentStatusManager()?.emitter.emit(ContentStatusEvents.ResourceSyncBegin);

        queue.onProgress(onProgress);
        queue.onError(onError);
        queue.onFinish(() => {
            onFinish();
            SlideDataServices.ContentStatusManager()?.emitter.emit(ContentStatusEvents.ResourceSyncEnd);
            PageExitManager.getInstance().release();
        });

        queue.enqueue(resources);
    }

    private batchUpdateResourceUrls(resources: ImageResource[], urls: string[]) {
        transaction(() => {
            resources.forEach((resource, i) => {
                resource.loadMeta().then((meta) => {
                    this.updateResourceMap(resource.id, {
                        src: urls[i],
                        ...meta,
                    });
                });
            });
        });
    }

    private updateResourceMap(
        id: string,
        meta: {
            src: string,
            width?: number,
            height?: number,
            size?: number,
            type?: string
        },
    ) {
        SlideDataServices.SDKDataServices.PresentationStoreService()?.getPresentation()
            .api.updateImageResourceMap(id, {
                src: meta.src,
                width: meta.width,
                height: meta.height,
                size: meta.size,
                type: meta.type,
            });
    }

    private performImageRecovery = async () => {
        await waitForReadyToEdit();

        const { globalPadId = '', userId = '' } = SlideDataServices.DocInfoManager() ?? {};
        this.imageRecovery = new ImageRecoveryManager([globalPadId ?? '@', userId ?? 'guest', 'images'], { enabled: this.enableImageRecovery });
        this.imageCreator = new ImageCreator(this.eventBus, this.imageRecovery);

        this.imageRecovery.recovery().then(async (items) => {
            // 没有未上传完成，需要恢复的图片，直接跳过
            if (!items.length) {
                return;
            }

            const quickLook = ImageQuickLookManager.getInstance();
            // 将 DB 中恢复的图片加载到秒看处理器
            const resources = await quickLook.bulkAddImageBlobs(
                items.map(item => item.blob),
                items.map(item => item.metadata.additions.id),
            );

            // 刷新 UI 层， 替换成新的秒看图
            this.batchUpdateResourceUrls(
                resources,
                resources.map(resource => resource.id),
            );

            // 重新异步上传未传完的图片
            this.uploadAndUpdateImages(resources);
        });
    };
}
